import React, { Component } from 'react';
import Markdown from 'react-markdown';
import { Redirect } from 'react-router-dom';
import { withRouter } from 'react-router';
import flatten from 'lodash.flatten';
import { branch, compose, renderComponent, withProps } from 'recompose';
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';

import LinkRenderer from 'components/utility/LinkRenderer/LinkRenderer';
import HeadingRenderer from 'components/utility/HeadingRenderer/HeadingRenderer';
import GlossaryToolbar from './GlossaryToolbar';
import GlossaryWrapper from './GlossaryWrapper';
import stringToSlug from 'helpers/stringToSlug';
import {
  Main,
  GlossaryTopic,
  GlossaryTermTitle,
  TableWrapper,
  EditField,
  DimmedText,
  EditModeWrapperAdmin,
  EditModeButtonAdmin,
} from './Glossary.style';
import ErrorDisplay from 'components/Error/ErrorDisplay/ErrorDisplay';
import GlossaryLoader from 'components/utility/ContentLoader/GlossaryLoader/GlossaryLoader';
import { IGlossaryProps } from './shared';
import MoreInfoGlossary from './MoreInfoGlossary';
import getRuntimeEnv from 'helpers/env';

type GlossaryTermProps = {
  /** Can this user edit the glossary? */
  userCanEdit?: boolean;
  /** The current glossary term's category */
  category: string;
  /** Glossary data returned by the API */
  data?: IGlossaryProps[];
  /** The glossary term we want to load from the complete set */
  term: string;
  /** Boolean indicating if this page is displayed in a modal */
  modal: boolean;
  /** Action to perform when closing the modal. Only required when the modal prop is set to true */
  hide?: (args: any) => void;
  /** An instance of the native Error object */
  error?: any;
  /** An object with additional error information, like a stack trace, or request headers. */
  errorInfo?: string | Record<string, any>;
  /** Updates the glossary */
  setGlossary?: any;
  /** Status code of the error. Based on this the component renders different content */
  status?: number;
  /** Current topic */
  /** Boolean indicating if this component is in loading state */
  loading: boolean;
  /** Is this term found in the Glossary? */
  termExists?: boolean;
  /** Glossary term topic */
  topic?: string;
  /** Glossary term name */
  name: string;
  /** Glossary term content */
  content?: string;
  /** Specifies wether the modal opens from one of the sides */
  openFromSide?: string;
};

type GlossaryTermState = {
  editMode: boolean;
};
/**
 * The page we show when you have entered a glossary search term.
 * @returns Either a Markdown component when the term is in the glossary,
 * or a Router Redirect to Index when it isn't found
 */
class GlossaryTerm extends Component<GlossaryTermProps, GlossaryTermState> {
  private body = React.createRef<any>();
  private title = React.createRef<any>();

  constructor(props: GlossaryTermProps) {
    super(props);

    this.state = {
      editMode: false,
    };
  }

  removeTerm = (): void => {
    const { data } = this.props;
    const newGlossary = cloneDeep(data);
    const categoryIndex = newGlossary?.findIndex(
      (category) => category.slug === this.props.category
    );
    if (newGlossary && categoryIndex) {
      const termIndex = newGlossary[categoryIndex].topics.findIndex(
        (term) => term.slug === this.props.term
      );

      if (
        window.confirm(
          'You are going to delete this term. Do you want to continue?'
        )
      ) {
        newGlossary[categoryIndex].topics.splice(termIndex, 1);

        this.storeGlossary(newGlossary);
      }
    }
  };

  saveTerm = (): void => {
    const { data, termExists } = this.props;

    const newGlossary = cloneDeep(data);
    const categoryIndex = newGlossary?.findIndex(
      (category) => category.slug === this.props.category
    );
    const titleValue = (this.title as any).current.value;
    const bodyValue = (this.body as any).current.value;

    const termIndex =
      newGlossary && categoryIndex
        ? newGlossary[categoryIndex].topics.findIndex(
            (term) => term.slug === this.props.term
          )
        : undefined;

    /** */
    if (titleValue && bodyValue && termIndex && categoryIndex && newGlossary) {
      if (termExists) {
        /** Term already exists */
        newGlossary[categoryIndex].topics[termIndex].name = titleValue;
        newGlossary[categoryIndex].topics[termIndex].content = bodyValue;
      } else {
        /** Term index doesn't exist */
        newGlossary[categoryIndex].topics = [
          ...newGlossary[categoryIndex].topics,
          {
            slug: this.props.term,
            name: titleValue,
            content: bodyValue,
          },
        ];
      }
    }

    this.storeGlossary(newGlossary);
  };

  storeGlossary(newGlossary?: IGlossaryProps[]): void {
    const { userCanEdit, setGlossary, data } = this.props;

    if (!isEqual(newGlossary, data)) {
      userCanEdit &&
        newGlossary &&
        setGlossary(newGlossary).then((res: any) => {
          if (!res.error) {
            this.setState({ editMode: false });
          }
        });
    } else {
      this.setState({ editMode: false });
    }
  }

  render(): JSX.Element {
    const {
      topic,
      name,
      content,
      modal,
      hide,
      loading,
      userCanEdit,
      termExists,
      error,
      errorInfo,
      openFromSide,
    } = this.props;

    const { editMode } = this.state;

    const EditTextArea = EditField as any;

    /**
     * Due to importing a string from JSON the newline character \n is handled
     * as a normal characters. Therefore we need to replace those so they can
     * be handled as newline characters in javascript.
     * @todo: find a better solution to handle this newline characters replacement.
     */
    const input = content && content.replace(/\\n/g, '\n');

    const showOpusVersion = stringToSlug(name) === 'about-opus';

    return (
      <GlossaryWrapper
        title={name ? `${name} - Glossary` : ''}
        modal={modal}
        loading={loading}
      >
        <GlossaryToolbar
          hide={hide}
          modal={modal}
          showClose={modal}
          borderPosition="bottom"
          openFromSide={openFromSide}
        />
        {!loading && userCanEdit && (
          <EditModeWrapperAdmin>
            {editMode && (
              <>
                <EditModeButtonAdmin
                  onClick={(): void => this.setState({ editMode: false })}
                >
                  Cancel
                </EditModeButtonAdmin>{' '}
                {termExists && (
                  <EditModeButtonAdmin onClick={this.removeTerm}>
                    Delete this glossary page
                  </EditModeButtonAdmin>
                )}
              </>
            )}
            {!editMode && (
              <EditModeButtonAdmin
                onClick={(): void => this.setState({ editMode: true })}
              >
                {termExists ? 'Edit' : 'Create glossary page'}
              </EditModeButtonAdmin>
            )}
          </EditModeWrapperAdmin>
        )}
        <Main modal={modal} role="main">
          {loading ? (
            <GlossaryLoader />
          ) : error && !editMode ? (
            /** Show an error when loading of the glossary fails */
            <ErrorDisplay error={error} errorInfo={errorInfo} />
          ) : (
            <>
              <GlossaryTopic>{topic}</GlossaryTopic>
              {editMode ? (
                <>
                  {error && (
                    /** Show an error when editting a glossary item fails. */
                    <ErrorDisplay error={error} errorInfo={errorInfo} />
                  )}
                  <EditField type="text" ref={this.title} defaultValue={name} />
                  <EditTextArea
                    as="textarea"
                    rows="15"
                    defaultValue={input}
                    ref={this.body}
                  />
                </>
              ) : termExists ? (
                <>
                  <GlossaryTermTitle>{name}</GlossaryTermTitle>
                  {content && (
                    <Markdown
                      source={input}
                      renderers={{
                        link: (props): JSX.Element => (
                          <LinkRenderer modal={modal} {...props} />
                        ),
                        heading: HeadingRenderer,
                        table: (props): JSX.Element => (
                          <TableWrapper>
                            {React.createElement('table', {}, props.children)}
                          </TableWrapper>
                        ),
                      }}
                    />
                  )}
                </>
              ) : (
                userCanEdit && (
                  <DimmedText>
                    This term does not yet exist.
                    <br />
                    Click on &apos;Create glossary page&apos; to add it to the
                    glossary.
                  </DimmedText>
                )
              )}
              {showOpusVersion && (
                <p>{`You are looking at Opus v${
                  getRuntimeEnv().REACT_APP_VERSION
                }`}</p>
              )}
            </>
          )}
        </Main>
        {userCanEdit && !loading && editMode && (
          <EditModeWrapperAdmin>
            <EditModeButtonAdmin onClick={this.saveTerm}>
              Publish
            </EditModeButtonAdmin>
          </EditModeWrapperAdmin>
        )}
        {openFromSide === undefined ? (
          <GlossaryToolbar hide={hide} modal={modal} borderPosition="top" />
        ) : (
          <MoreInfoGlossary />
        )}
      </GlossaryWrapper>
    );
  }
}

const NoMatch = ({
  modal,
  location,
}: {
  modal: any;
  location: any;
}): JSX.Element => (
  <Redirect to={{ ...location, pathname: '/glossary', state: { modal } }} />
);
const hasNoMatch = (hasNoMatch: any): any =>
  branch(hasNoMatch, renderComponent(NoMatch));

const enhance = compose(
  withRouter,
  withProps(({ data, term, category, ...rest }) => {
    if (!data) {
      return { ...rest };
    }

    /**
     * This will make sure that every category has a slug.
     * If a slug doesn't exist, it will be created based on the category name.  */
    const dataWithCategorySlugs = data.map((category: any): any => ({
      ...category,
      slug: category.slug ? category.slug : stringToSlug(category.name),
    }));

    /** Add the topic to every term, so we can show it on the term page  */
    const terms = dataWithCategorySlugs.map((category: any): any =>
      category.topics.map((term: any) => ({
        ...term,
        topic: category.name,
        category: category.slug,
      }))
    );

    /** In order to do a lookup in all the groups flatten all the terms in the groups in one array so it's easier to loop */
    const allTerms = flatten(terms);

    /** Does the search term match the slug in the traversed array item? */
    const matchesTerm = (item: any): any => {
      return item.slug === term && item.category === category;
    };

    /** If we found a matching term we will start the Markdown renderer and display the results */
    const match: any = allTerms.find(matchesTerm);
    const termExists = Boolean(match);

    return { ...rest, ...match, termExists };
  }),
  hasNoMatch(
    ({
      loading,
      success,
      topic,
      category,
      data,
      error,
      userCanEdit,
    }: {
      loading: boolean;
      success: boolean;
      topic: any;
      category: any;
      data: any;
      error: any;
      userCanEdit: boolean;
    }) => {
      const categoryExists = Boolean(
        data && data.find((cat: any) => cat.slug === category)
      );

      /** Redirect if the user isn't able to edit a non-exisiting term in an existing category. */
      return (
        !(userCanEdit && categoryExists) &&
        success &&
        !loading &&
        !topic &&
        !error
      );
    }
  )
);

export default enhance(GlossaryTerm as any);
